import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSupportGuidesForAdmin,
  getSupportGuides,
  getPhotoGuide,
  createSupportGuide,
  updateSupportGuide,
  deleteSupportGuide,
  setPhotoGuide,
} from '@/services/support/supportGuideService';
import type {
  SupportGuideDto,
  CreateSupportGuideRequest,
  UpdateSupportGuideRequest,
} from '@/types/supportGuide';

export const supportGuidesQueryKeys = {
  admin: () => ['supportGuides', 'admin'] as const,
  photoGuide: () => ['supportGuide', 'photoGuide'] as const,
  public: (params?: { type?: string }) => ['supportGuides', params] as const,
};

export function useSupportGuidesForAdmin() {
  return useQuery({
    queryKey: supportGuidesQueryKeys.admin(),
    queryFn: getSupportGuidesForAdmin,
    staleTime: 2 * 60 * 1000,
  });
}

export function usePhotoGuide() {
  return useQuery({
    queryKey: supportGuidesQueryKeys.photoGuide(),
    queryFn: getPhotoGuide,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSupportGuidesPublic(params?: { type?: string; category?: string }) {
  return useQuery({
    queryKey: supportGuidesQueryKeys.public(params),
    queryFn: () => getSupportGuides(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateSupportGuide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateSupportGuideRequest) => createSupportGuide(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportGuides'] });
      queryClient.invalidateQueries({ queryKey: ['supportGuide'] });
    },
  });
}

export function useUpdateSupportGuide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateSupportGuideRequest }) =>
      updateSupportGuide(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportGuides'] });
      queryClient.invalidateQueries({ queryKey: ['supportGuide'] });
    },
  });
}

export function useDeleteSupportGuide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSupportGuide(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportGuides'] });
      queryClient.invalidateQueries({ queryKey: ['supportGuide'] });
    },
  });
}

export function useSetPhotoGuide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => setPhotoGuide(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportGuides'] });
      queryClient.invalidateQueries({ queryKey: ['supportGuide'] });
    },
  });
}
